import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { WHATSAPP_NUMBER } from '@constants/contact'
import Accordion from 'react-bootstrap/Accordion'
import { FaInfoCircle, FaCheckCircle, FaFileInvoice, FaFileUpload, FaTruck, FaMoneyBillWave, FaWhatsapp, FaEye, FaTag } from 'react-icons/fa'
import { FaCircleQuestion } from 'react-icons/fa6'
import { MdPayment } from 'react-icons/md'
import { Badge } from 'react-bootstrap'
import { IoTrash } from 'react-icons/io5'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import AccordionHeaderContent from './AccordionHeaderContent'
import NotStartedStepMessage from './NotStartedStepMessage'
import Pix from '../Tabs/Pix/Pix'
import Ted from '../Tabs/Ted/Ted'
import Pagar from '../Tabs/Pagar/Pagar'
import { getStepState } from '../utils/getStepState'
import IUser from '@interfaces/IUser'
import IOrder from '@interfaces/IOrder'
import PaymentProofStatusEnum from '@enums/PaymentProofStatusEnum'
import OrderStatusEnum from '@enums/OrderStatusEnum'
import * as $Order from '@services/Order'

interface AccordionPaymentsAndReceiptsProps {
  sampleStepProgress: number
  transportStepProgress: number

  samplePaymentMethodTab: string
  setSamplePaymentMethodTab: (tab: string) => void

  user: IUser | null
  inProgressOrder: IOrder | null

  comprovantePagamentoFile: File | null
  transportadoraFile: File | null
  fileUploadWarning: string
  onFileChange: (event: ChangeEvent<HTMLInputElement>, fileType: 'comprovantePagamento' | 'transportadora') => void
  onRemoveFile: (fileType: 'comprovantePagamento' | 'transportadora') => void
  onSubmitPaymentProofs: (proofType: 'sample' | 'transport' | 'both') => void
  isOrderInSubstatus: boolean
}

const AccordionPaymentsAndReceipts: React.FC<AccordionPaymentsAndReceiptsProps> = ({
  sampleStepProgress,
  transportStepProgress,
  samplePaymentMethodTab,
  setSamplePaymentMethodTab,
  user,
  inProgressOrder,
  comprovantePagamentoFile,
  transportadoraFile,
  fileUploadWarning,
  onFileChange,
  onRemoveFile,
  onSubmitPaymentProofs,
  isOrderInSubstatus,
}) => {
  const [nfeSelection, setNfeSelection] = useState<'patient' | 'partner'>(inProgressOrder?.nfe_receiver || 'patient')
  const [pickingFile, setPickingFile] = useState<string | null>(null)
  const [showVideoModal, setShowVideoModal] = useState(false)

  const SweetAlert = withReactContent(Swal)

  useEffect(() => {
    if (inProgressOrder?.nfe_receiver) {
      setNfeSelection(inProgressOrder.nfe_receiver)
    }
  }, [inProgressOrder?.nfe_receiver])

  const handleNfeSelectionChange = useCallback((value: 'patient' | 'partner') => {
    if (!inProgressOrder?.internal_id || nfeSelection === value) return

    setNfeSelection(value)

    SweetAlert.fire({
      title: 'Aguarde...',
      text: 'Atualizando destinatário da NFe',
      icon: 'info',
      showConfirmButton: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
      allowEnterKey: false,
      didOpen: () => SweetAlert.showLoading(),
    })

    $Order.update(inProgressOrder.internal_id, { nfe_receiver: value }).then(() => {
      SweetAlert.fire({
        title: 'Sucesso!',
        text: 'Destinatário da NFe atualizado com sucesso',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
      })
    }).catch((error: any) => {
      setNfeSelection(nfeSelection)
      SweetAlert.fire({
        title: 'Erro!',
        text: error.response?.data?.message || 'Erro ao atualizar destinatário da NFe',
        icon: 'error',
        confirmButtonText: 'Ok',
      })
    })
  }, [inProgressOrder, nfeSelection, SweetAlert])

  const handleFileInputClick = useCallback((fileType: string) => {
    setPickingFile(fileType)
  }, [])

  useEffect(() => {
    if (!pickingFile) return
    const resetPicking = () => setPickingFile(null)
    window.addEventListener('focus', resetPicking, { once: true })
    return () => window.removeEventListener('focus', resetPicking)
  }, [pickingFile])

  const hasPartner = !!(user?.patient?.partner_name && user?.patient?.partner_document)

  const shippingPayment = inProgressOrder?.shipping_type
  const isProSeedShipping = shippingPayment === 'pro-seed'
  const isClinicShipping = shippingPayment === 'clinic'
  const isShippingCompany = shippingPayment === 'shipping-company'

  // Transport payment not required for pro-seed and clinic
  const isTransportPaymentRequired = isShippingCompany

  // Payment is only completed if both sample and (if required) transport proofs are uploaded/approved
  const isSampleProofDone = !!inProgressOrder?.sample_payment_proof_status && inProgressOrder.sample_payment_proof_status === PaymentProofStatusEnum.APPROVED
  const isTransportProofDone = !isTransportPaymentRequired || (!!inProgressOrder?.transport_payment_proof_status && inProgressOrder.transport_payment_proof_status === PaymentProofStatusEnum.APPROVED)
  const isPaymentCompleted = isSampleProofDone && isTransportProofDone

  const isSamplePaymentProofUploaded = !!inProgressOrder?.sample_payment_proof_status
  const isTransportPaymentProofUploaded = !!inProgressOrder?.transport_payment_proof_status
  const isAllRequiredProofsUploaded = isSamplePaymentProofUploaded && (isTransportPaymentRequired ? isTransportPaymentProofUploaded : true)
  const combinedProgress = useMemo(() => {

    if (isPaymentCompleted || isAllRequiredProofsUploaded) return 100

    if (sampleStepProgress > 0 || transportStepProgress > 0) return 50

    return 0
  }, [isPaymentCompleted, isAllRequiredProofsUploaded, sampleStepProgress, transportStepProgress])

  const stepState = getStepState(combinedProgress)

  const PAYMENT_UPLOAD_ALLOWED_STATUSES = [
    OrderStatusEnum.WAITING_PAYMENT,
    OrderStatusEnum.WAITING_PAYMENT_TRANSPORT,
    OrderStatusEnum.WAITING_PAYMENT_VERIFICATION,
  ]

  const canUploadPaymentProof = !!inProgressOrder?.status &&
    PAYMENT_UPLOAD_ALLOWED_STATUSES.includes(inProgressOrder.status as OrderStatusEnum)

  const isUnlocked = useMemo(() => {
    return !!inProgressOrder?.clinic && sampleStepProgress > 0
  }, [inProgressOrder, sampleStepProgress])

  const clinicShippingData = useMemo(() => {
    if (!inProgressOrder?.shipping_type) return null

    const shippingPayment = inProgressOrder.shipping_type
    const transportValue = inProgressOrder?.transport_value ?? 0

    let res: {
      title: string
      description: string
      price: number | string
    }

    switch (shippingPayment) {
    case 'pro-seed':
      res = {
        title: 'Pagamento é feito para o Pro-Seed:',
        description: 'O custo de transporte será adicionado ao valor na amostra de sêmen no painel da receptora.',
        price: transportValue,
      }
      break
    case 'shipping-company':
      res = {
        title: 'Pagamento é feito diretamente para a transportadora:',
        description: 'O custo do transporte é apresentado no pedido, mas não é adicionado ao custo da amostra de sêmen.',
        price: transportValue,
      }
      break
    case 'clinic':
      res = {
        title: 'Pagamento é feito através da clínica:',
        description: 'Toda a gestão do processo logístico é de responsabilidade da clínica.',
        price: 0,
      }
      break
    }

    res.price = Number(res.price).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

    return res
  }, [inProgressOrder])

  const paymentValues = useMemo(() => {
    const subtotal = inProgressOrder?.subtotal ?? 0
    const transportValue = inProgressOrder?.transport_value ?? 0

    const discountPercent = samplePaymentMethodTab === 'Pagar' ? 0 : 5
    const discountAmount = (subtotal * discountPercent) / 100

    // Don't include transport value in total for clinic shipping
    const total = subtotal - discountAmount + (isClinicShipping ? 0 : transportValue)

    return {
      subtotal,
      transportValue,
      discountPercent,
      discountAmount,
      total,
      subtotalFormatted: subtotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
      transportValueFormatted: transportValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
      discountAmountFormatted: discountAmount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
      totalFormatted: total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
    }
  }, [inProgressOrder, samplePaymentMethodTab, isClinicShipping])

  return (
    <>
      <Accordion.Item eventKey="2" className={`${stepState}-state`}>
        <Accordion.Header>
          <AccordionHeaderContent
            stepState={stepState}
            progress={combinedProgress}
            title={<span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
              Pagamentos e Comprovantes
              <FaCircleQuestion
                style={{ color: '#FA5A84', cursor: 'pointer', fontSize: '14px', flexShrink: 0 }}
                onClick={(e) => { e.stopPropagation(); setShowVideoModal(true) }}
              />
            </span>}
          />
        </Accordion.Header>

        <Accordion.Body>
          <div className="check-sample p-3">
            {!isUnlocked && (
              <NotStartedStepMessage />
            )}

            {isUnlocked && (
              <div className="select-payment">
                <div className="row">
                  {/* Row 1: Resumo de Valores | Emissão da NFe */}
                  <div className="col-12 col-lg-6 mb-3 pr-lg-2">
                    <div className="box details p-4 h-100 shadow-sm border-0" style={{ borderRadius: '12px', backgroundColor: '#fff' }}>
                      <div className="title mb-2 d-flex align-items-center">
                        <div className="d-flex align-items-center justify-content-center" style={{
                          width: '36px',
                          height: '36px',
                          flexShrink: 0,
                          borderRadius: '8px',
                          background: 'linear-gradient(135deg, #FA5A84 0%, #FF8BA7 100%)',
                          marginRight: '12px'
                        }}
                        >
                          <MdPayment size={20} style={{ color: '#fff' }} />
                        </div>
                        <h5 className="mb-0 font-weight-bold">Resumo de Valores</h5>
                      </div>
                      <div className="payment-summary">
                        <table style={{ width: '100%' }}>
                          <thead></thead>
                          <tbody>
                            <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                              <th style={{ padding: '12px 0', fontWeight: '500', color: '#495057' }}>Amostra de Sêmen</th>
                              <td style={{ padding: '12px 0', textAlign: 'center' }}>
                                <Badge bg="primary" className="px-2 py-1">01</Badge>
                              </td>
                              <td style={{ padding: '12px 0', textAlign: 'right', fontWeight: '600' }}>{paymentValues.subtotalFormatted}</td>
                            </tr>
                            <tr style={{ borderBottom: isClinicShipping ? '2px solid #dee2e6' : '1px solid #f0f0f0' }}>
                              <th style={{ padding: '12px 0', fontWeight: '500', color: '#495057' }}>
                                Desconto na Amostra de Sêmen
                              </th>
                              <td style={{ padding: '12px 0', textAlign: 'center' }}>
                                {paymentValues.discountPercent > 0 && (
                                  <Badge style={{ backgroundColor: '#4d61aa' }} className="px-2 py-1">{paymentValues.discountPercent}%</Badge>
                                )}
                              </td>
                              <td className="cupom" style={{ padding: '12px 0', textAlign: 'right', fontWeight: '600', color: '#4d61aa' }}>-{paymentValues.discountAmountFormatted}</td>
                            </tr>
                            {!isClinicShipping && (
                              <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                                <th style={{ padding: '12px 0', fontWeight: '500', color: '#495057' }}>
                                  Transporte
                                </th>
                                <td style={{ padding: '12px 0', textAlign: 'center' }}>
                                  <Badge bg="secondary" className="px-2 py-1">01</Badge>
                                </td>
                                <td style={{ padding: '12px 0', textAlign: 'right', fontWeight: '600' }}>{paymentValues.transportValueFormatted}</td>
                              </tr>
                            )}
                          </tbody>
                          <tfoot>
                            <tr>
                              <th style={{ padding: '16px 0 0 0', fontSize: '1.1rem', fontWeight: '700', color: '#212529' }}>Total a Pagar</th>
                              <td></td>
                              <td style={{ padding: '16px 0 0 0', textAlign: 'right', fontSize: '1.25rem', fontWeight: '700', color: '#FA5A84' }}>{paymentValues.totalFormatted}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-lg-6 mb-3 pl-lg-2">
                    <div className="box NF p-4 h-100 shadow-sm border-0" style={{ borderRadius: '12px', backgroundColor: '#fff' }}>
                      <div className="title">
                        <div className="d-flex align-items-center mb-2">
                          <div className="d-flex align-items-center justify-content-center" style={{
                            width: '36px',
                            height: '36px',
                            flexShrink: 0,
                            borderRadius: '8px',
                            background: 'linear-gradient(135deg, #FA5A84 0%, #FF8BA7 100%)',
                            marginRight: '12px'
                          }}
                          >
                            <FaFileInvoice size={18} style={{ color: '#fff' }} />
                          </div>
                          <h5 className="mb-0 font-weight-bold">
                            Emissão de NFe
                          </h5>
                        </div>
                        <p className="mb-0 text-muted" style={{ padding: '20px 0px' }}>
                          A NFe será emitida para o titular selecionado abaixo.
                        </p>
                      </div>
                      <div className="content row">
                        <div className={`line-content col-12 ${hasPartner ? 'col-sm-6 mb-3 mb-sm-0' : ''}`}>
                          <div className="title mb-3 font-weight-600" style={{ fontSize: '0.95rem', color: '#495057' }}>Paciente:</div>
                          <label htmlFor="NFePatient" className="d-block p-3 mb-0" style={{
                            cursor: 'pointer',
                            borderRadius: '8px',
                            border: nfeSelection === 'patient' ? '2px solid #4d61aa' : '2px solid #e9ecef',
                            backgroundColor: nfeSelection === 'patient' ? '#E8EEFF' : '#fff',
                            transition: 'all 0.2s'
                          }}
                          >
                            <div className="d-flex align-items-start">
                              <input
                                type="radio"
                                id="NFePatient"
                                className="input-select checkbox-round mr-3 mt-1"
                                name="input-radio"
                                checked={nfeSelection === 'patient'}
                                onChange={() => handleNfeSelectionChange('patient')}
                                disabled={isPaymentCompleted || isOrderInSubstatus}
                              />
                              <div className="patient flex-grow-1">
                                <div className="text name font-weight-600 mb-1" style={{ fontSize: '0.95rem' }}>{user?.name || '-'}</div>
                                <div className="text cpf text-muted" style={{ fontSize: '0.85rem' }}>{user?.document || '-'}</div>
                              </div>
                            </div>
                          </label>
                        </div>
                        {hasPartner && (
                          <div className="line-content col-12 col-sm-6">
                            <div className="title mb-3 font-weight-600" style={{ fontSize: '0.95rem', color: '#495057' }}>Companheiro:</div>
                            <label htmlFor="NFePartner" className="d-block p-3 mb-0" style={{
                              cursor: 'pointer',
                              borderRadius: '8px',
                              border: nfeSelection === 'partner' ? '2px solid #4d61aa' : '2px solid #e9ecef',
                              backgroundColor: nfeSelection === 'partner' ? '#E8EEFF' : '#fff',
                              transition: 'all 0.2s'
                            }}
                            >
                              <div className="d-flex align-items-start">
                                <input
                                  type="radio"
                                  id="NFePartner"
                                  className="input-select checkbox-round mr-3 mt-1"
                                  name="input-radio"
                                  checked={nfeSelection === 'partner'}
                                  onChange={() => handleNfeSelectionChange('partner')}
                                  disabled={isPaymentCompleted || isOrderInSubstatus}
                                />
                                <div className="patient flex-grow-1">
                                  <div className="text name font-weight-600 mb-1" style={{ fontSize: '0.95rem' }}>{user?.patient?.partner_name || '-'}</div>
                                  <div className="text cpf text-muted" style={{ fontSize: '0.85rem' }}>{user?.patient?.partner_document || '-'}</div>
                                </div>
                              </div>
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Row 2: Custo de Transporte (full width) */}
                  {clinicShippingData && (
                    <div className="col-12 mb-2">
                      <div className="box payment p-4 shadow-sm border-0" style={{ borderRadius: '12px', backgroundColor: '#f8f9fa' }}>
                        <div className="d-flex align-items-start">
                          <div className="d-flex align-items-center justify-content-center" style={{
                            width: '42px',
                            height: '42px',
                            borderRadius: '10px',
                            background: 'linear-gradient(135deg, #FA5A84 0%, #FF8BA7 100%)',
                            marginRight: '16px',
                            flexShrink: 0
                          }}
                          >
                            <FaTruck size={20} style={{ color: '#fff' }} />
                          </div>
                          <div className="text flex-grow-1">
                            <h5 className="mb-2 font-weight-bold">Custo de Transporte</h5>
                            <p className="mb-0" style={{ lineHeight: '1.6', color: '#495057' }}>
                              <strong>{clinicShippingData.title}</strong> {clinicShippingData.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Row 3: Formas de pagamento da Amostra de Sêmen | Formas de pagamento da Transportadora */}
                  {!isPaymentCompleted && (
                    <>
                      <div className="col-12 mb-3">
                        <div className="box payment p-4 h-100 shadow-sm border-0" style={{ borderRadius: '12px', backgroundColor: '#fff' }}>
                          <div className="title mb-3">
                            <div className="text">
                              <div className="d-flex align-items-center mb-2">
                                <div className="d-flex align-items-center justify-content-center" style={{
                                  width: '36px',
                                  height: '36px',
                                  flexShrink: 0,
                                  borderRadius: '8px',
                                  background: 'linear-gradient(135deg, #FA5A84 0%, #FF8BA7 100%)',
                                  marginRight: '12px'
                                }}
                                >
                                  <FaMoneyBillWave size={18} style={{ color: '#fff' }} />
                                </div>
                                <h5 className="mb-0 font-weight-bold">Formas de pagamento da <span style={{ color: '#FA5A84' }}>Amostra de Sêmen</span></h5>
                              </div>
                              <p className="mb-0 text-muted" style={{ fontSize: '0.9rem' }}>Escolha uma forma de pagamento para prosseguir</p>
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-12 col-lg-6">
                              <label htmlFor="SamplePix" className="d-block p-3" style={{
                                cursor: 'pointer',
                                borderRadius: '8px',
                                border: samplePaymentMethodTab === 'Pix' ? '2px solid #4d61aa' : '2px solid #e9ecef',
                                backgroundColor: samplePaymentMethodTab === 'Pix' ? '#E8EEFF' : '#fff',
                                transition: 'all 0.2s'
                              }}
                              >
                                <div className="d-flex align-items-start">
                                  <input
                                    type="radio"
                                    id="SamplePix"
                                    className="input-select checkbox-round mr-3 mt-1"
                                    checked={samplePaymentMethodTab === 'Pix'}
                                    onChange={() => setSamplePaymentMethodTab('Pix')}
                                    disabled={isOrderInSubstatus}
                                  />
                                  <div>
                                    <div className="font-weight-700 mb-1"><b>PIX / TED</b></div>
                                    <small className="d-flex align-items-center">
                                      <Badge className="badge-discount mr-2 px-3 py-1">5% OFF</Badge>
                                      Desconto aplicado
                                    </small>
                                  </div>
                                </div>
                              </label>
                            </div>
                            <div className="col-12 col-lg-6">
                              <label htmlFor="SamplePagarme" className="d-block p-3" style={{
                                cursor: 'pointer',
                                borderRadius: '8px',
                                border: samplePaymentMethodTab === 'Pagar' ? '2px solid #4d61aa' : '2px solid #e9ecef',
                                backgroundColor: samplePaymentMethodTab === 'Pagar' ? '#E8EEFF' : '#fff',
                                transition: 'all 0.2s'
                              }}
                              >
                                <div className="d-flex align-items-start">
                                  <input
                                    type="radio"
                                    id="SamplePagarme"
                                    className="input-select checkbox-round mr-3 mt-1"
                                    checked={samplePaymentMethodTab === 'Pagar'}
                                    onChange={() => setSamplePaymentMethodTab('Pagar')}
                                    disabled={isOrderInSubstatus}
                                  />
                                  <div>
                                    <div className="font-weight-600 mb-1"><b>Pagar.me</b></div>
                                    <small className="text-muted">Parcelamento em 3x (sem juros) ou até 12x com acréscimo</small>
                                  </div>
                                </div>
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Row 4: Dados para pagamento da Amostra de Sêmen | Dados para pagamento da Transportadora */}
                  {(!isPaymentCompleted || (isTransportPaymentRequired && (!inProgressOrder?.transport_payment_proof_status || inProgressOrder.transport_payment_proof_status === PaymentProofStatusEnum.PENDING))) && (
                    <>
                      <div className={`col-12 ${isTransportPaymentRequired ? 'col-lg-6 pr-lg-2' : ''} mb-3`}>
                        <div className="box payment p-4 h-100 shadow-sm border-0 d-flex flex-column" style={{ borderRadius: '12px', backgroundColor: '#fff' }}>
                          <div className="title mb-3">
                            <div className="d-flex align-items-center">
                              <div className="d-flex align-items-center justify-content-center" style={{
                                width: '36px',
                                height: '36px',
                                flexShrink: 0,
                                borderRadius: '8px',
                                background: 'linear-gradient(135deg, #FA5A84 0%, #FF8BA7 100%)',
                                marginRight: '12px'
                              }}
                              >
                                <MdPayment size={20} style={{ color: '#fff' }} />
                              </div>
                              <h5 className="mb-0 font-weight-bold">Dados para Pagamento da <span style={{ color: '#FA5A84' }}>Amostra de Sêmen</span></h5>
                            </div>
                          </div>
                          <div className="line-content flex-grow-1 d-flex flex-column">
                            {samplePaymentMethodTab === 'Pix' && <Pix price={!isShippingCompany ? paymentValues.total : (paymentValues.subtotal - paymentValues.discountAmount)} />}
                            {samplePaymentMethodTab === 'Ted' && <Ted />}
                            {samplePaymentMethodTab === 'Pagar' && <Pagar inProgressOrder={inProgressOrder} price={!isShippingCompany ? paymentValues.total : (paymentValues.subtotal - paymentValues.discountAmount)} />}
                          </div>

                          {/* File Upload Section */}
                          {samplePaymentMethodTab !== 'Pagar' && (
                            <div className="mt-3 pt-3" style={{ borderTop: '1px solid #f0f0f0' }}>
                              <div className="d-flex align-items-center justify-content-between mb-3">
                                <h6 className="font-weight-bold mb-0" style={{ color: '#495057', fontSize: '0.95rem' }}>
                                  Comprovante de Pagamento
                                </h6>
                                {inProgressOrder?.sample_payment_proof_status && (
                                  <span className="px-2 py-1" style={{
                                    borderRadius: '6px',
                                    fontWeight: 600,
                                    fontSize: '0.80em',
                                    backgroundColor: inProgressOrder.sample_payment_proof_status === PaymentProofStatusEnum.APPROVED
                                      ? '#FA5A84'
                                      : inProgressOrder.sample_payment_proof_status === PaymentProofStatusEnum.PENDING
                                        ? '#E6EAF6'
                                        : '#FFD6E3',
                                    color: inProgressOrder.sample_payment_proof_status === PaymentProofStatusEnum.APPROVED
                                      ? '#fff'
                                      : inProgressOrder.sample_payment_proof_status === PaymentProofStatusEnum.PENDING
                                        ? '#4d61aa'
                                        : '#FA5A84',
                                  }}
                                  >
                                    <FaTag size={12} className="mr-1" style={{ marginTop: '-2px' }} />
                                    {inProgressOrder.sample_payment_proof_status === PaymentProofStatusEnum.APPROVED
                                      ? 'Aprovado'
                                      : inProgressOrder.sample_payment_proof_status === PaymentProofStatusEnum.PENDING
                                        ? 'Em Análise'
                                        : 'Rejeitado'}
                                  </span>
                                )}
                              </div>
                              {inProgressOrder?.sample_payment_proof_status === PaymentProofStatusEnum.APPROVED ? (
                                <div className="p-3" style={{
                                  borderRadius: '8px',
                                  backgroundColor: '#d4edda',
                                  border: '1px solid #28a745',
                                }}
                                >
                                  <div className="d-flex align-items-center justify-content-between">
                                    <div className="d-flex align-items-center">
                                      <FaCheckCircle size={18} className="mr-2" style={{ color: '#28a745', flexShrink: 0 }} />
                                      <p className="mb-0 font-weight-600" style={{ fontSize: '0.85rem', color: '#155724' }}>Comprovante aprovado pelo financeiro.</p>
                                    </div>
                                    {inProgressOrder?.sample_payment_proof_url && (
                                      <a
                                        href={inProgressOrder.sample_payment_proof_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="btn btn-sm px-3 py-1 ml-2 d-flex align-items-center"
                                        style={{
                                          backgroundColor: '#28a745',
                                          color: '#fff',
                                          borderRadius: '6px',
                                          fontWeight: 600,
                                          fontSize: '0.80rem',
                                          textDecoration: 'none',
                                          flexShrink: 0
                                        }}
                                      >
                                        <FaEye size={14} className="mr-1" /> Ver
                                      </a>
                                    )}
                                  </div>
                                </div>
                              ) : inProgressOrder?.sample_payment_proof_status === PaymentProofStatusEnum.PENDING ? (
                                <div className="px-3 py-2" style={{
                                  borderRadius: '8px',
                                  backgroundColor: '#E8EEFF',
                                  border: '1px solid #4d61aa',
                                }}
                                >
                                  <div className="d-flex align-items-center justify-content-between">
                                    <div className="d-flex align-items-center">
                                      <FaInfoCircle size={18} className="mr-2" style={{ color: '#4d61aa', flexShrink: 0 }} />
                                      <div>
                                        <p className="mb-0 font-weight-600" style={{ fontSize: '0.85rem', color: '#4d61aa' }}>Comprovante enviado e em análise.</p>
                                        <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>Aguarde a análise antes de enviar outro arquivo.</p>
                                      </div>
                                    </div>
                                    {inProgressOrder?.sample_payment_proof_url && (
                                      <a
                                        href={inProgressOrder.sample_payment_proof_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="btn btn-sm px-3 py-1 ml-2"
                                        style={{
                                          backgroundColor: '#4d61aa',
                                          color: '#fff',
                                          borderRadius: '6px',
                                          fontWeight: 600,
                                          fontSize: '0.80rem',
                                          textDecoration: 'none',
                                          flexShrink: 0
                                        }}
                                      >
                                        <FaEye size={14} className="mr-1" /> Ver
                                      </a>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <>
                                  {comprovantePagamentoFile ? (
                                    <>
                                      <div className="p-3" style={{
                                        borderRadius: '8px',
                                        border: '2px solid #4d61aa',
                                        backgroundColor: '#E8EEFF',
                                      }}
                                      >
                                        <div className="d-flex align-items-center justify-content-between">
                                          <div className="d-flex align-items-center flex-grow-1" style={{ minWidth: 0 }}>
                                            <FaCheckCircle size={18} className="mr-2" style={{ color: '#4d61aa', flexShrink: 0 }} />
                                            <p className="mb-0 text-truncate font-weight-600" style={{ fontSize: '0.85rem', color: '#495057' }}>
                                              {comprovantePagamentoFile.name}
                                            </p>
                                          </div>
                                          <button
                                            className="btn btn-link p-0 ml-2"
                                            onClick={() => onRemoveFile('comprovantePagamento')}
                                            style={{ flexShrink: 0, color: '#dc3545' }}
                                          >
                                            <IoTrash size={18} />
                                          </button>
                                        </div>
                                      </div>
                                      <button
                                        className="btn btn-lg py-3 mt-3 w-100"
                                        style={{
                                          background: 'linear-gradient(135deg, #4d61aa 0%, #6b7fc1 100%)',
                                          color: '#fff',
                                          border: 'none',
                                          borderRadius: '10px',
                                          fontWeight: 'bold',
                                          fontSize: '1rem',
                                          cursor: !canUploadPaymentProof ? 'not-allowed' : 'pointer',
                                          transition: 'all 0.2s',
                                          boxShadow: '0 4px 12px rgba(77, 97, 170, 0.3)',
                                        }}
                                        onClick={() => onSubmitPaymentProofs('sample')}
                                        disabled={!canUploadPaymentProof}
                                      >
                                        <div className="d-flex align-items-center justify-content-center">
                                          <FaCheckCircle size={18} className="mr-2" />
                                          Enviar Comprovante
                                        </div>
                                      </button>
                                    </>
                                  ) : (
                                    <label
                                      htmlFor="comprovantePagamentoInput"
                                      className="d-flex justify-content-center align-items-center py-2 px-3 mb-0"
                                      style={{
                                        cursor: !canUploadPaymentProof ? 'not-allowed' : 'pointer',
                                        borderRadius: '8px',
                                        border: pickingFile === 'comprovantePagamento'
                                          ? '2px solid #4d61aa'
                                          : '2px dashed #ced4da',
                                        backgroundColor: pickingFile === 'comprovantePagamento'
                                          ? '#E8EEFF'
                                          : '#f8f9fa',
                                        transition: 'all 0.2s',
                                        width: '100%',
                                        opacity: !canUploadPaymentProof ? 0.6 : 1,
                                      }}
                                      onClick={() => canUploadPaymentProof && handleFileInputClick('comprovantePagamento')}
                                    >
                                      <div className="d-flex align-items-center">
                                        <FaFileUpload size={18} className="mr-3" style={{ color: '#4d61aa' }} />
                                        <div className="text-left">
                                          <p className="mb-0 font-weight-600" style={{ fontSize: '0.85rem', color: '#495057' }}>
                                            {pickingFile === 'comprovantePagamento' ? 'Abrindo seletor de arquivo...' : 'Clique ou arraste o arquivo'}
                                          </p>
                                          <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>PDF, JPG, JPEG ou PNG (Máximo 3MB)</p>
                                        </div>
                                      </div>
                                      <input
                                        id="comprovantePagamentoInput"
                                        type="file"
                                        accept=".pdf, .jpg, .jpeg, .png"
                                        style={{ display: 'none' }}
                                        onChange={(e) => onFileChange(e, 'comprovantePagamento')}
                                        disabled={!canUploadPaymentProof}
                                      />
                                    </label>
                                  )}
                                  {fileUploadWarning && (
                                    <div className="mt-2 p-2" style={{
                                      backgroundColor: '#fff3cd',
                                      border: '1px solid #ffc107',
                                      borderRadius: '6px',
                                      fontSize: '0.85rem',
                                      color: '#856404'
                                    }}
                                    >
                                      <div className="d-flex align-items-start">
                                        <FaInfoCircle size={16} className="mr-2 mt-1" style={{ flexShrink: 0, color: '#ffc107' }} />
                                        <span>{fileUploadWarning}</span>
                                      </div>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {isTransportPaymentRequired && (
                        <div className="col-12 col-lg-6 mb-3 pl-lg-2">
                          <div className="box payment p-4 h-100 shadow-sm border-0 d-flex flex-column" style={{ borderRadius: '12px', backgroundColor: '#fff', position: 'relative' }}>
                            <div className="title mb-3">
                              <div className="d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center">
                                  <div className="d-flex align-items-center justify-content-center" style={{
                                    width: '36px',
                                    height: '36px',
                                    flexShrink: 0,
                                    borderRadius: '8px',
                                    background: 'linear-gradient(135deg, #FA5A84 0%, #FF8BA7 100%)',
                                    marginRight: '12px'
                                  }}
                                  >
                                    <FaTruck size={18} style={{ color: '#fff' }} />
                                  </div>
                                  <h5 className="mb-0 font-weight-bold">Dados para Pagamento da <span style={{ color: '#FA5A84' }}>Transportadora</span></h5>
                                </div>
                              </div>
                            </div>

                            {isProSeedShipping ? (
                              <div className="line-content p-3" style={{
                                borderRadius: '8px',
                                backgroundColor: '#E8EEFF',
                                border: '1px solid #4d61aa'
                              }}
                              >
                                <div className="d-flex align-items-start">
                                  <FaCheckCircle size={18} className="mr-2 mt-1" style={{ flexShrink: 0, color: '#4d61aa' }} />
                                  <p className="mb-0">O pagamento do frete é tratado junto ao Pro-Seed.</p>
                                </div>
                              </div>
                            ) : (
                              <div className="line-content flex-grow-1 d-flex flex-column">
                                <Pix shippingCompany={inProgressOrder?.shipping_company} price={paymentValues.transportValue} />
                              </div>
                            )}

                            {/* File Upload Section */}
                            {!isProSeedShipping && (
                              <div className="mt-3 pt-3" style={{ borderTop: '1px solid #f0f0f0' }}>
                                <div className="d-flex align-items-center justify-content-between mb-3">
                                  <h6 className="font-weight-bold mb-0" style={{ color: '#495057', fontSize: '0.95rem' }}>
                                    Comprovante de Pagamento
                                  </h6>
                                  {inProgressOrder?.transport_payment_proof_status && (
                                    <span className="px-2 py-1" style={{
                                      borderRadius: '6px',
                                      fontWeight: 600,
                                      fontSize: '0.80em',
                                      backgroundColor: inProgressOrder.transport_payment_proof_status === PaymentProofStatusEnum.APPROVED
                                        ? '#FA5A84'
                                        : inProgressOrder.transport_payment_proof_status === PaymentProofStatusEnum.PENDING
                                          ? '#E6EAF6'
                                          : '#FFD6E3',
                                      color: inProgressOrder.transport_payment_proof_status === PaymentProofStatusEnum.APPROVED
                                        ? '#fff'
                                        : inProgressOrder.transport_payment_proof_status === PaymentProofStatusEnum.PENDING
                                          ? '#4d61aa'
                                          : '#FA5A84',
                                    }}
                                    >
                                      <FaTag size={12} className="mr-1" style={{ marginTop: '-2px' }} />
                                      {inProgressOrder.transport_payment_proof_status === PaymentProofStatusEnum.APPROVED
                                        ? 'Aprovado'
                                        : inProgressOrder.transport_payment_proof_status === PaymentProofStatusEnum.PENDING
                                          ? 'Em Análise'
                                          : 'Rejeitado'}
                                    </span>
                                  )}
                                </div>
                                {inProgressOrder?.transport_payment_proof_status === PaymentProofStatusEnum.APPROVED ? (
                                  <div className="p-3" style={{
                                    borderRadius: '8px',
                                    backgroundColor: '#d4edda',
                                    border: '1px solid #28a745',
                                  }}
                                  >
                                    <div className="d-flex align-items-center justify-content-between">
                                      <div className="d-flex align-items-center">
                                        <FaCheckCircle size={18} className="mr-2" style={{ color: '#28a745', flexShrink: 0 }} />
                                        <p className="mb-0 font-weight-600" style={{ fontSize: '0.85rem', color: '#155724' }}>Comprovante aprovado pelo financeiro.</p>
                                      </div>
                                      {inProgressOrder?.transport_payment_proof_url && (
                                        <a
                                          href={inProgressOrder.transport_payment_proof_url}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="btn btn-sm px-3 py-1 ml-2"
                                          style={{
                                            backgroundColor: '#28a745',
                                            color: '#fff',
                                            borderRadius: '6px',
                                            fontWeight: 600,
                                            fontSize: '0.80rem',
                                            textDecoration: 'none',
                                            flexShrink: 0
                                          }}
                                        >
                                          <FaEye size={14} className="mr-1" /> Ver
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                ) : inProgressOrder?.transport_payment_proof_status === PaymentProofStatusEnum.PENDING ? (
                                  <div className="px-3 py-2" style={{
                                    borderRadius: '8px',
                                    backgroundColor: '#E8EEFF',
                                    border: '1px solid #4d61aa',
                                  }}
                                  >
                                    <div className="d-flex align-items-center justify-content-between">
                                      <div className="d-flex align-items-center">
                                        <FaInfoCircle size={18} className="mr-2" style={{ color: '#4d61aa', flexShrink: 0 }} />
                                        <div>
                                          <p className="mb-0 font-weight-600" style={{ fontSize: '0.85rem', color: '#4d61aa' }}>Comprovante enviado e em análise.</p>
                                          <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>Aguarde a análise antes de enviar outro arquivo.</p>
                                        </div>
                                      </div>
                                      {inProgressOrder?.transport_payment_proof_url && (
                                        <a
                                          href={inProgressOrder.transport_payment_proof_url}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="btn btn-sm px-3 py-1 ml-2"
                                          style={{
                                            backgroundColor: '#4d61aa',
                                            color: '#fff',
                                            borderRadius: '6px',
                                            fontWeight: 600,
                                            fontSize: '0.80rem',
                                            textDecoration: 'none',
                                            flexShrink: 0
                                          }}
                                        >
                                          <FaEye size={14} className="mr-1" /> Ver
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                ) : (!inProgressOrder?.transport_payment_proof_status || inProgressOrder.transport_payment_proof_status === PaymentProofStatusEnum.REJECTED) && (
                                  <>
                                    {transportadoraFile ? (
                                      <>
                                        <div className="p-3" style={{
                                          borderRadius: '8px',
                                          border: '2px solid #4d61aa',
                                          backgroundColor: '#E8EEFF',
                                        }}
                                        >
                                          <div className="d-flex align-items-center justify-content-between">
                                            <div className="d-flex align-items-center flex-grow-1" style={{ minWidth: 0 }}>
                                              <FaCheckCircle size={18} className="mr-2" style={{ color: '#4d61aa', flexShrink: 0 }} />
                                              <p className="mb-0 text-truncate font-weight-600" style={{ fontSize: '0.85rem', color: '#495057' }}>
                                                {transportadoraFile.name}
                                              </p>
                                            </div>
                                            <button
                                              className="btn btn-link p-0 ml-2"
                                              onClick={() => onRemoveFile('transportadora')}
                                              style={{ flexShrink: 0, color: '#dc3545' }}
                                            >
                                              <IoTrash size={18} />
                                            </button>
                                          </div>
                                        </div>
                                        <button
                                          className="btn btn-lg py-3 mt-3 w-100"
                                          style={{
                                            background: 'linear-gradient(135deg, #4d61aa 0%, #6b7fc1 100%)',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '10px',
                                            fontWeight: 'bold',
                                            fontSize: '1rem',
                                            cursor: !canUploadPaymentProof ? 'not-allowed' : 'pointer',
                                            transition: 'all 0.2s',
                                            boxShadow: '0 4px 12px rgba(77, 97, 170, 0.3)',
                                          }}
                                          onClick={() => onSubmitPaymentProofs('transport')}
                                          disabled={!canUploadPaymentProof}
                                        >
                                          <div className="d-flex align-items-center justify-content-center">
                                            <FaCheckCircle size={18} className="mr-2" />
                                            Enviar Comprovante
                                          </div>
                                        </button>
                                      </>
                                    ) : (
                                      <label
                                        htmlFor="transportadoraInput"
                                        className="d-flex justify-content-center align-items-center py-2 px-3 mb-0"
                                        style={{
                                          cursor: !canUploadPaymentProof ? 'not-allowed' : 'pointer',
                                          borderRadius: '8px',
                                          border: pickingFile === 'transportadora'
                                            ? '2px solid #4d61aa'
                                            : '2px dashed #ced4da',
                                          backgroundColor: pickingFile === 'transportadora'
                                            ? '#E8EEFF'
                                            : '#f8f9fa',
                                          transition: 'all 0.2s',
                                          width: '100%',
                                          opacity: !canUploadPaymentProof ? 0.6 : 1,
                                        }}
                                        onClick={() => canUploadPaymentProof && handleFileInputClick('transportadora')}
                                      >
                                        <div className="d-flex align-items-center">
                                          <FaFileUpload size={18} className="mr-3" style={{ color: '#4d61aa' }} />
                                          <div className="text-left">
                                            <p className="mb-0 font-weight-600" style={{ fontSize: '0.85rem', color: '#495057' }}>
                                              {pickingFile === 'transportadora' ? 'Abrindo seletor de arquivo...' : 'Clique ou arraste o arquivo'}
                                            </p>
                                            <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>PDF, JPG, JPEG ou PNG (Máximo 3MB)</p>
                                          </div>
                                        </div>
                                        <input
                                          id="transportadoraInput"
                                          type="file"
                                          accept=".pdf, .jpg, .jpeg, .png"
                                          style={{ display: 'none' }}
                                          onChange={(e) => onFileChange(e, 'transportadora')}
                                          disabled={!canUploadPaymentProof}
                                        />
                                      </label>
                                    )}
                                    {fileUploadWarning && (
                                      <div className="mt-2 p-2" style={{
                                        backgroundColor: '#fff3cd',
                                        border: '1px solid #ffc107',
                                        borderRadius: '6px',
                                        fontSize: '0.85rem',
                                        color: '#856404'
                                      }}
                                      >
                                        <div className="d-flex align-items-start">
                                          <FaInfoCircle size={16} className="mr-2 mt-1" style={{ flexShrink: 0, color: '#ffc107' }} />
                                          <span>{fileUploadWarning}</span>
                                        </div>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* Row 5: Dúvidas ou Questões Financeiras (full width) */}
                  <div className="col-12">
                    <div className="box payment p-4 mb-0 shadow-sm border-0" style={{
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #FA5A84 0%, #FF8BA7 100%)',
                      color: '#fff'
                    }}
                    >
                      <div className="d-flex align-items-center justify-content-between flex-wrap">
                        <div className="text flex-grow-1 mb-3 mb-md-0">
                          <div className="d-flex align-items-center mb-2">
                            <div className="d-flex align-items-center justify-content-center" style={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '10px',
                              backgroundColor: 'rgba(255, 255, 255, 0.2)',
                              marginRight: '14px'
                            }}
                            >
                              <FaInfoCircle size={20} className="text-white" />
                            </div>
                            <h5 className="mb-0 font-weight-bold text-white">Dúvidas ou Questões Financeiras?</h5>
                          </div>
                          <p className="mb-0 text-white" style={{ marginLeft: '54px', opacity: 0.95, lineHeight: '1.5' }}>
                            Entre em contato com nosso departamento financeiro para esclarecimentos
                          </p>
                        </div>
                        <a
                          target="_blank"
                          href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Já fiz um pedido no portal e tenho dúvidas sobre o pagamento.')}`}
                          rel="noreferrer"
                          className="btn btn-light btn-lg px-5 py-3"
                          style={{
                            borderRadius: '10px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            textDecoration: 'none',
                            transition: 'all 0.2s',
                            fontWeight: 'bold',
                            fontSize: '1rem',
                            minWidth: '285px'
                          }}
                        >
                          <div className="d-flex align-items-center">
                            <FaWhatsapp size={20} className="mr-2" style={{ color: '#25D366' }} />
                            Falar no WhatsApp
                          </div>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Accordion.Body>
      </Accordion.Item>
      {showVideoModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowVideoModal(false)}>
          <div style={{ position: 'relative', width: 'min(1280px, 90vw)' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
              <iframe style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }} src="https://www.youtube.com/embed/W4hrqqWDpEY" title="YouTube video player" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen></iframe>
            </div>
            <button style={{ position: 'absolute', top: '-10px', right: '-10px', background: 'red', color: 'white', border: 'none', borderRadius: '50%', width: '30px', height: '30px', cursor: 'pointer' }} onClick={() => setShowVideoModal(false)}>×</button>
          </div>
        </div>
      )}
    </>
  )
}

export default AccordionPaymentsAndReceipts
